import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import Loader from 'react-loader-spinner';
import { FiChevronLeft, FiChevronRight, FiCopy, FiSearch } from 'react-icons/fi';
import { API_URLS } from '../constants/config';
import { useI18n } from '../utilities/i18n';
import { authorizedPost, getApiErrorMessage } from '../utilities/stadiumsApi';
import { useChampionships } from '../utilities/championships';
import { IfCanWrite, useAdminAccess } from '../utilities/adminAccess';
import { champFields } from '../utilities/tournamentsApi';
import {
  getPlayerPhotoUrl,
  getTeamPhotoUrl,
  isPlayerCaptain,
  playerCardPath,
  playerDisplayName,
  playerPositionLabel,
} from '../utilities/playersApi';
import {
  DUPLICATE_FIELD_OPTIONS,
  DUPLICATE_GROUPS_PAGE_SIZE,
  canMergeDuplicateDraft,
  emptyDuplicateDraft,
  extractDuplicateGroups,
  formatDuplicateBirthday,
  mergeDuplicatePayload,
  orderedDuplicateFields,
  removePlayersFromDuplicateGroups,
  toggleDuplicateField,
} from '../utilities/playerDuplicatesApi';
import CustomModal from '../components/CustomModal';
import StadiumCrumbs from '../components/StadiumCrumbs';
import defaultPlayerImage from '../images/defaultAvatar.png';
import defaultTeamLogo from '../images/defaultTeamLogo.png';
import '../styles/stadiumsManagement.css';
import '../styles/playerDuplicates.css';

export default function PlayerDuplicates() {
  const dispatch = useDispatch();
  const { t, lang } = useI18n();
  const { champId, selected: championship, loading: champsLoading } = useChampionships();
  const { canWrite, canReadChamp } = useAdminAccess('players');

  // По умолчанию — вся платформа (кросс-чемп дубли)
  const [scopeAll, setScopeAll] = useState(true);
  const [fields, setFields] = useState(() => orderedDuplicateFields());
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [groups, setGroups] = useState([]);
  const [totalGroups, setTotalGroups] = useState(0);
  const [totalPlayers, setTotalPlayers] = useState(0);
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(true);
  const [mergingKey, setMergingKey] = useState('');
  const [message, setMessage] = useState({ text: '', type: '' });
  const [confirm, setConfirm] = useState(null);
  const searchTimer = useRef(null);
  const requestIdRef = useRef(0);
  const pageRef = useRef(0);
  pageRef.current = page;

  const filterChampId = scopeAll ? null : champId;
  const canLoadChampScope = Boolean(filterChampId) && canReadChamp;
  const canLoad = scopeAll || canLoadChampScope;

  const loadGroups = async (search, pageIndex = 0) => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setMessage({ text: '', type: '' });
    setConfirm(null);

    if (!canLoad) {
      setGroups([]);
      setTotalGroups(0);
      setTotalPlayers(0);
      setDrafts({});
      setLoading(false);
      setMessage({
        text: champsLoading ? '' : t(!champId ? 'dupNeedChamp' : 'champNoAccess'),
        type: champsLoading ? '' : 'error',
      });
      return false;
    }

    try {
      const body = {
        ...(filterChampId ? champFields(filterChampId) : {}),
        fields: orderedDuplicateFields(fields),
        limit: DUPLICATE_GROUPS_PAGE_SIZE,
        offset: pageIndex * DUPLICATE_GROUPS_PAGE_SIZE,
      };
      const query = String(search || '').trim();
      if (query) body.search = query;
      const response = await authorizedPost(dispatch, API_URLS.getAdminDuplicatePlayers, body);
      if (requestId !== requestIdRef.current) return false;
      const packed = extractDuplicateGroups(response.data);
      setGroups(packed.groups);
      setTotalGroups(packed.total_groups);
      setTotalPlayers(packed.total_players);
      setDrafts({});
      // После merge страница могла опустеть — отойти на предыдущую
      if (!packed.groups.length && pageIndex > 0 && packed.total_groups > 0) {
        const prevPage = Math.max(0, pageIndex - 1);
        setPage(prevPage);
        pageRef.current = prevPage;
        return loadGroups(search, prevPage);
      }
      return true;
    } catch (error) {
      if (requestId !== requestIdRef.current) return false;
      setGroups([]);
      setTotalGroups(0);
      setTotalPlayers(0);
      setDrafts({});
      setMessage({ text: getApiErrorMessage(error, t('dupLoadError')), type: 'error' });
      return false;
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  };

  // Смена фильтров — всегда с первой страницы
  useEffect(() => {
    if (!scopeAll && champsLoading) return undefined;
    if (searchTimer.current) clearTimeout(searchTimer.current);
    const delay = searchQuery ? 350 : 0;
    searchTimer.current = setTimeout(() => {
      setPage(0);
      pageRef.current = 0;
      loadGroups(searchQuery, 0);
    }, delay);
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
  }, [searchQuery, fields, scopeAll, champId, champsLoading, canReadChamp]);

  const goToPage = (nextPage) => {
    const safe = Math.max(0, nextPage);
    setPage(safe);
    pageRef.current = safe;
    loadGroups(searchQuery, safe);
  };

  const groupKey = (group, index) => (
    group.map((player) => player.id).join('-') || `g-${index}`
  );

  const draftOf = (key) => drafts[key] || emptyDuplicateDraft();

  const patchDraft = (key, updater) => {
    setDrafts((prev) => {
      const current = prev[key] || emptyDuplicateDraft();
      return { ...prev, [key]: updater(current) };
    });
  };

  const toggleSelected = (key, playerId) => {
    patchDraft(key, (draft) => {
      const selected = draft.selectedIds.includes(playerId)
        ? draft.selectedIds.filter((id) => id !== playerId)
        : draft.selectedIds.concat(playerId);
      return {
        selectedIds: selected,
        keepId: draft.keepId === playerId && !selected.includes(playerId) ? null : draft.keepId,
      };
    });
  };

  const setKeep = (key, playerId) => {
    patchDraft(key, (draft) => ({
      keepId: playerId,
      selectedIds: draft.selectedIds.includes(playerId)
        ? draft.selectedIds
        : draft.selectedIds.concat(playerId),
    }));
  };

  const requestMerge = (key, group) => {
    if (!canWrite) return;
    const draft = draftOf(key);
    if (!canMergeDuplicateDraft(draft)) return;
    const payload = mergeDuplicatePayload(draft);
    const keep = group.find((player) => player.id === payload.keep_player_id);
    setConfirm({
      key,
      payload,
      keepName: playerDisplayName(keep) || `#${payload.keep_player_id}`,
    });
  };

  const confirmMerge = async () => {
    if (!confirm || mergingKey) return;
    const { key, payload } = confirm;
    setMergingKey(key);
    setConfirm(null);
    setMessage({ text: '', type: '' });
    try {
      await authorizedPost(dispatch, API_URLS.mergeAdminDuplicatePlayers, payload);
      setGroups((prev) => removePlayersFromDuplicateGroups(prev, payload.duplicate_ids));
      setDrafts((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      const reloaded = await loadGroups(searchQuery, pageRef.current);
      if (reloaded) setMessage({ text: t('dupMerged'), type: 'success' });
    } catch (error) {
      setMessage({ text: getApiErrorMessage(error, t('dupMergeError')), type: 'error' });
    } finally {
      setMergingKey('');
    }
  };

  const totalPages = Math.max(1, Math.ceil((totalGroups || 0) / DUPLICATE_GROUPS_PAGE_SIZE));
  const pageOffset = page * DUPLICATE_GROUPS_PAGE_SIZE;
  const showPager = totalGroups > DUPLICATE_GROUPS_PAGE_SIZE;

  return (
    <div className="layoutWrapper st-layout">
      <div className="matchCenterWrapper st-page dup-list-page">
        <div className="matchCenterHeader">
          <div className="st-header">
            <StadiumCrumbs items={[{ label: t('navDuplicates') }]} />
          </div>
        </div>

        <div className="st-toolbar">
          <div className="st-search">
            <FiSearch className="st-search-icon" />
            <input
              type="text"
              className="st-search-input"
              placeholder={t('dupSearchPh')}
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
            />
            {searchQuery ? (
              <button type="button" className="st-search-clear" onClick={() => setSearchQuery('')}>
                ×
              </button>
            ) : null}
          </div>

          <div className="dup-scope">
            <span className="dup-fields-label">{t('dupScope')}</span>
            <label className="st-check">
              <input
                type="radio"
                name="dup-scope"
                checked={scopeAll}
                onChange={() => setScopeAll(true)}
              />
              {t('dupScopeAll')}
            </label>
            <label className={`st-check${!champId ? ' is-disabled' : ''}`}>
              <input
                type="radio"
                name="dup-scope"
                checked={!scopeAll}
                disabled={!champId}
                onChange={() => setScopeAll(false)}
              />
              {championship?.name
                ? t('dupScopeChampNamed', { name: championship.name })
                : t('dupScopeChamp')}
            </label>
          </div>

          <div className="dup-fields">
            <span className="dup-fields-label">{t('dupFields')}</span>
            {DUPLICATE_FIELD_OPTIONS.map((option) => (
              <label key={option.id} className={`st-check${option.locked ? ' is-disabled' : ''}`}>
                <input
                  type="checkbox"
                  checked={fields.includes(option.id)}
                  disabled={option.locked}
                  onChange={() => setFields((prev) => toggleDuplicateField(prev, option.id))}
                />
                {t(option.labelKey)}
              </label>
            ))}
          </div>
          <p className="dup-hint">{t('dupFieldsHint')}</p>
          <div className="st-meta">
            <strong>{t('dupFound', { groups: totalGroups, players: totalPlayers })}</strong>
            {showPager ? (
              <span className="dup-page-meta">
                {t('dupPageOf', { page: page + 1, pages: totalPages })}
              </span>
            ) : null}
          </div>
        </div>

        {message.text ? <div className={`st-message ${message.type}`}>{message.text}</div> : null}

        <div className="st-scroll">
          <div className="st-content">
            <div className="st-block">
              <h2 className="st-block-header">{t('navDuplicates')}</h2>
              {loading ? (
                <div className="st-loading">
                  <Loader type="Oval" color="#3ad1a0" height={48} width={48} />
                  <p>{t('dupLoading')}</p>
                </div>
              ) : groups.length ? (
                <>
                  <div className="dup-groups">
                    {groups.map((group, index) => {
                      const key = groupKey(group, index);
                      const draft = draftOf(key);
                      const merging = mergingKey === key;
                      const canMerge = canWrite && canMergeDuplicateDraft(draft) && !merging;
                      return (
                        <section key={key} className="dup-group">
                          <div className="dup-group-head">
                            <h3 className="dup-group-title">
                              {t('dupGroupTitle', {
                                n: pageOffset + index + 1,
                                count: group.length,
                              })}
                            </h3>
                            <span className="dup-group-meta">{t('dupMergeHint')}</span>
                          </div>
                          <div className="dup-players">
                            {group.map((player) => {
                              const selected = draft.selectedIds.includes(player.id);
                              const keep = draft.keepId === player.id;
                              const birthday = formatDuplicateBirthday(player.birthday, lang);
                              const position = playerPositionLabel(player);
                              const champName = player.championat_name || '';
                              return (
                                <article
                                  key={player.id}
                                  className={`dup-player${selected ? ' is-selected' : ''}${keep ? ' is-keep' : ''}`}
                                >
                                  <div className="dup-player-controls">
                                    <label className={`st-check${canWrite ? '' : ' is-disabled'}`}>
                                      <input
                                        type="checkbox"
                                        checked={selected}
                                        disabled={!canWrite || merging}
                                        onChange={() => toggleSelected(key, player.id)}
                                      />
                                      {t('dupInclude')}
                                    </label>
                                    <label className={`dup-keep${canWrite ? '' : ' is-disabled'}`}>
                                      <input
                                        type="radio"
                                        name={`dup-keep-${key}`}
                                        checked={keep}
                                        disabled={!canWrite || merging}
                                        onChange={() => setKeep(key, player.id)}
                                      />
                                      {t('dupKeep')}
                                    </label>
                                  </div>
                                  <img
                                    src={getPlayerPhotoUrl(player.image) || defaultPlayerImage}
                                    alt=""
                                    className="dup-photo"
                                    onError={(event) => { event.currentTarget.src = defaultPlayerImage; }}
                                  />
                                  <div className="dup-player-body">
                                    <Link className="dup-player-name" to={playerCardPath(player.id)}>
                                      {playerDisplayName(player) || '—'}
                                      <span className="dup-player-id">{t('dupId', { id: player.id })}</span>
                                    </Link>
                                    <div className="dup-player-meta">
                                      {birthday ? <span>{birthday}</span> : null}
                                      {position ? <span>{position}</span> : null}
                                      {champName ? (
                                        <span className="dup-champ">{champName}</span>
                                      ) : null}
                                    </div>
                                    {player.teams.length ? (
                                      <div className="dup-teams">
                                        {player.teams.map((team) => (
                                          <span
                                            key={`${player.id}-${team.team_id}-${team.application_id || ''}`}
                                            className="dup-team"
                                          >
                                            <img
                                              src={getTeamPhotoUrl(team.team_image) || defaultTeamLogo}
                                              alt=""
                                              onError={(event) => { event.currentTarget.src = defaultTeamLogo; }}
                                            />
                                            <span className="dup-team-name">
                                              {team.team_name || '—'}
                                              {team.number != null && team.number !== '' ? ` · ${team.number}` : ''}
                                            </span>
                                            {isPlayerCaptain(team, player) ? (
                                              <span className="pl-captain-badge">{t('plCaptain')}</span>
                                            ) : null}
                                          </span>
                                        ))}
                                      </div>
                                    ) : (
                                      <div className="dup-player-meta">{t('dupNoTeams')}</div>
                                    )}
                                  </div>
                                </article>
                              );
                            })}
                          </div>
                          <div className="dup-merge-actions">
                            <IfCanWrite entity="players">
                              <button
                                type="button"
                                className="st-btn st-btn-primary"
                                disabled={!canMerge}
                                onClick={() => requestMerge(key, group)}
                              >
                                {merging ? t('dupLoading') : t('dupMerge')}
                              </button>
                            </IfCanWrite>
                          </div>
                        </section>
                      );
                    })}
                  </div>
                  {showPager ? (
                    <div className="dup-pager">
                      <button
                        type="button"
                        className="st-btn st-btn-secondary"
                        disabled={loading || page <= 0}
                        onClick={() => goToPage(page - 1)}
                      >
                        <FiChevronLeft /> {t('dupPrevPage')}
                      </button>
                      <span className="dup-pager-label">
                        {t('dupPageOf', { page: page + 1, pages: totalPages })}
                      </span>
                      <button
                        type="button"
                        className="st-btn st-btn-secondary"
                        disabled={loading || page + 1 >= totalPages}
                        onClick={() => goToPage(page + 1)}
                      >
                        {t('dupNextPage')} <FiChevronRight />
                      </button>
                    </div>
                  ) : null}
                </>
              ) : (
                <div className="st-empty">
                  <FiCopy size={48} />
                  <h3>{t('dupEmpty')}</h3>
                  <p>{t('dupEmptyHint')}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        <CustomModal
          styles={{ width: 460, height: 'auto', paddingLeft: 32, paddingRight: 32 }}
          visible={Boolean(confirm)}
          closeModal={() => setConfirm(null)}
        >
          <div className="st-delete-modal">
            <div className="customModalHeader">{t('dupMergeTitle')}</div>
            <p>{t('dupMergeText', { name: confirm?.keepName || '' })}</p>
            <div className="st-delete-actions">
              <button type="button" className="btn-modal btn-secondary-modal" onClick={() => setConfirm(null)}>
                {t('cancel')}
              </button>
              <button type="button" className="btn-modal btn-danger-modal" onClick={confirmMerge}>
                {t('dupMerge')}
              </button>
            </div>
          </div>
        </CustomModal>
      </div>
    </div>
  );
}
